import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import paletteOutline from '../../../assets/images/palette-outline.svg'
import Loader from '../shared/Loader'
import {
  ExportHeader,
  CollectInfo,
  ErrorAlert,
  CustomExportForm
} from '../shared/Export'
import { addFormInputs, getDateRangeParams } from '../ExportModal/utils/exportHelpers'
import { formatDate } from '../../utils/dateHelpers'

const EMPTY_PREFERENCES = {}

const PhotosExport = ({
  collectId,
  draftId = null,
  currentSectionId = null,
  returnUrl = null,
  savedPreferences = EMPTY_PREFERENCES,
  ignoreFilters = false
}) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [ensemble, setEnsemble] = useState(null)
  const [selectedQuestions, setSelectedQuestions] = useState({})

  const getRedirectBackUrl = useCallback(() => {
    if (returnUrl)
      return returnUrl
    if (draftId)
      return `/collects/${collectId}/drafts/${draftId}/photo_gallery`
    return `/collects/${collectId}/drafts/`
  }, [collectId, draftId, returnUrl])

  const handleExport = useCallback(() => {
    const form = document.getElementById('export-form')

    const sectionIds = Object.entries(selectedQuestions)
      .filter(([, questions]) => questions.length > 0)
      .map(([sectionId]) => sectionId)
    const questionIds = Object.values(selectedQuestions).flat()

    // Clear existing hidden inputs
    form.querySelectorAll('input[type="hidden"]:not([name="authenticity_token"])').forEach(input => {
      input.remove()
    })

    sectionIds.forEach(sectionId => addFormInputs(form, { 'section_ids[]': sectionId }))
    questionIds.forEach(questionId => addFormInputs(form, { 'question_ids[]': questionId }))
    addFormInputs(form, {
      // eslint-disable-next-line camelcase
      return_url: returnUrl ?? null,
      // eslint-disable-next-line camelcase
      ignore_filters: ignoreFilters ? 'true' : null,
      ...getDateRangeParams()
    })

    if (window.plausible)
      window.plausible('Collect Photos Exported')

    form.submit()
  }, [selectedQuestions, returnUrl, ignoreFilters])

  const handleSubmit = useCallback(event => {
    event.preventDefault()
    handleExport()
  }, [handleExport])

  const getTotalSelectedQuestions = useCallback(() => Object.values(selectedQuestions).reduce(
    (total, questions) => total + questions.length, 0
  ), [selectedQuestions])

  const selectAllQuestions = useCallback(sections => {
    const selection = {}
    sections.forEach(section => {
      selection[section.id] = section.questions.map(question => question.id)
    })
    return selection
  }, [])

  const applyPreferences = useCallback((sectionsWithPhotos, preferences) => {
    const newSelection = {}

    sectionsWithPhotos.forEach(section => {
      const sectionQuestions = section.questions
        .filter(question => preferences.question_ids.includes(question.id))
        .map(question => question.id)

      if (sectionQuestions.length > 0)
        newSelection[section.id] = sectionQuestions
    })

    setSelectedQuestions(newSelection)
  }, [])

  const getExportUrl = useCallback(() => {
    const baseUrl = `/collects/${collectId}`
    return draftId ? `${baseUrl}/drafts/${draftId}/photos_exports` : `${baseUrl}/photos_exports`
  }, [collectId, draftId])

  useEffect(() => {
    const headers = {
      'accept': 'application/json',
      'Content-Type': 'application/json'
    }

    fetch(`/collects/${collectId}.json`, { method: 'GET', headers })
      .then(response => {
        if (!response.ok)
          throw new Error('Erreur lors du chargement des données')

        return response.json()
      })
      .then(responseData => {
        const sectionsWithPhotos = responseData.sections.map(section => ({
          ...section,
          questions: section.questions.filter(question => question.context === 'photos')
        })).filter(section => section.questions.length > 0)

        const filteredData = { ...responseData, sections: sectionsWithPhotos }
        setData(filteredData)

        const foundEnsemble = responseData.ensembles.find(item => item.id === responseData.ensemble_id)
        setEnsemble(foundEnsemble)

        if (currentSectionId) {
          const initialSelection = {}
          sectionsWithPhotos.forEach(section => {
            if (section.id === currentSectionId)
              initialSelection[section.id] = section.questions.map(question => question.id)
            else
              initialSelection[section.id] = []
          })
          setSelectedQuestions(initialSelection)
        } else if (savedPreferences && savedPreferences.photos)
          applyPreferences(sectionsWithPhotos, savedPreferences.photos)
        else
          setSelectedQuestions(selectAllQuestions(sectionsWithPhotos))

        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }, [collectId, currentSectionId, savedPreferences, applyPreferences, selectAllQuestions])

  if (loading)
    return <Loader text="Chargement des données..." size="small" />

  if (error)
    return <ErrorAlert message={error} />

  if (!data)
    return null
  const currentDraft = data.drafts.find(draftItem => draftItem.id === draftId)
  const startingAt = currentDraft?.starting_at ? formatDate(currentDraft.starting_at) : null

  return (
    <div className="modal-content border">
      <ExportHeader
        title="Personnaliser l'export photos"
        icon={paletteOutline}
        iconAlt="Customize"
        cancelUrl={getRedirectBackUrl()}
      />
      <div className="modal-body">
        <CollectInfo
          collectName={data.name}
          startingAt={startingAt}
          ensembleName={ensemble?.name}
        />
        <CustomExportForm
          exportUrl={getExportUrl()}
          sections={data.sections}
          selectedQuestions={selectedQuestions}
          title="Sélectionnez les sections et questions photos à exporter"
          onQuestionChange={setSelectedQuestions}
          onSubmit={handleSubmit}
        />
      </div>
      <div className="modal-footer">
        <div className="d-flex justify-content-end align-items-center w-100">
          <button
            type="submit"
            form="export-form"
            className="btn btn-success"
            disabled={getTotalSelectedQuestions() === 0}
          >
            Exporter les photos
          </button>
        </div>
      </div>
    </div>
  )
}

PhotosExport.propTypes = {
  collectId: PropTypes.number.isRequired
}

export default PhotosExport
